import {
  SearchspringFacet,
  SearchspringMerchandising,
  SearchspringPagination,
  SearchspringSearchResult,
  SearchspringSorting,
} from "@app/types"

import { getCurrentStoreConfig } from "@app/config"

import { session } from "./session"
/**
 * Config Functions
 * ---
 * Functions for getting Searchspring config values.
 */

const RANGE_INDICATOR = "<>"

const config = getCurrentStoreConfig()

export type SearchspringCategory = keyof typeof config.searchspring

export function getSearchspringDomain(category: SearchspringCategory) {
  switch (category) {
    case "collections":
      return window.location.host + "/collections?view=ss-feed"
    case "pages":
      return window.location.host + "/pages/faqs?view=ss-feed"
    case "products":
    default:
      return window.location.href
  }
}

export function getSearchspringSiteId(category?: SearchspringCategory) {
  return category ? config.searchspring[category] : config.searchspring.products
}

/**
 * SearchspringParams
 * ---
 * Class designed to handle standardized formatting for any
 * URL manipulation SS might need for request building.
 * Extends on URLSearch Params.
 */

export class SearchspringParams extends URLSearchParams {
  siteId
  domain

  constructor(category: SearchspringCategory) {
    super()

    this.siteId = getSearchspringSiteId(category)
    this.domain = getSearchspringDomain(category)
  }

  // Local Params

  setSiteIDParam() {
    this.set("siteId", this.siteId)
    return this
  }

  setDomainParam() {
    this.set("domain", this.domain)
    return this
  }

  // Session Params

  setViewedProducts() {
    const viewedProducts = session.viewedProducts.value
    if (viewedProducts) this.set("lastViewed", viewedProducts)
  }

  setShopperID() {
    const shopperId = session.shopperId.value
    if (shopperId) this.set("shopper", shopperId)
  }

  setUserID() {
    const userId = session.userId.value
    if (userId) this.set("userId", userId)
  }

  // Global Params

  setCartItems() {
    // const cart = Alpine.store("cart")
    const cart = []
    const items = cart.cartFetched ? cart.items : window.SHOPIFY_DATA.cart.items
    const skus = items.map((item) => item.sku).join(",")
    if (skus) this.set("cart", skus)
  }

  // Helper Methods

  setParams(params: URLSearchParams) {
    for (const [key, values] of Object.entries(params)) {
      this.set(key, values)
    }

    return this
  }

  //   Filtering Methods

  setSort(sort: string) {
    // Remove previous sorting
    // For some reason, directly using this.keys() or this.entries() randomly misses last key
    const keys = Array.from(this.keys())
    for (const key of keys) {
      if (key.startsWith("sort")) this.delete(key)
    }

    // Add new sorting
    const [field, direction] = sort.split(":")
    const key = `sort.${field}`
    this.set(key, direction)
  }

  setFilters(filters: string[]) {
    // Remove previous filters
    // For some reason, directly using this.keys() or this.entries() randomly misses last key
    const keys = Array.from(this.keys())
    for (const key of keys) {
      if (key.startsWith("filter")) this.delete(key)
    }

    for (const filter of filters) {
      // ponytail: two-element destructuring, so a value containing ":" is
      // silently truncated rather than erroring. Clean for every current facet
      // value, including all 92 raw x-fabric tags. Split on the first ":" only
      // if a colon ever turns up in tag data.
      const [field, value] = filter.split(":")
      const key = `filter.${field}`

      // Aribitrary string delimiter that communicates this is a range
      // TODO: define this as a delimiter somewhere
      if (value.includes(RANGE_INDICATOR)) {
        const [low, high] = value.split(RANGE_INDICATOR)
        this.append(`${key}.low`, low)
        this.append(`${key}.high`, high)
      } else {
        this.append(key, value)
      }
    }

    return this
  }
}

/**
 * SearchspringFetcher
 * ---
 * Class designed to handle standardized fetch requests for Searchspring.
 */

export class SearchspringFetcher<Response> {
  params
  controller

  constructor(category: SearchspringCategory) {
    this.params = new SearchspringParams(category)
    this.controller = new AbortController()
  }

  generateHeaders() {
    return new Headers({
      Accept: "application/json",
      "searchspring-user-id": session.userId.value,
      "searchspring-session-id": session.sessionId.value,
      "searchspring-page-load-id": session.pageLoadId.value,
    })
  }

  generateRequestInit() {
    return {
      method: "GET",
      headers: this.generateHeaders(),
      signal: this.controller.signal ?? null,
    }
  }

  generateOrigin() {
    return `https://${this.params.siteId}.a.searchspring.io`
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  updateParams(options?: unknown) {
    this.params.setSiteIDParam()
    // Beacon 2.0 integration flag - tells Searchspring to return responseId
    // and enables automatic tracking-event generation.
    this.params.set("beacon", "true")
  }

  generateUrl(endpoint?: string) {
    const origin = this.generateOrigin()
    const params = this.params.toString()
    return new URL(`${origin}${endpoint}?${params}`)
  }

  refreshAbortController() {
    this.controller = new AbortController()
  }

  async fetch() {
    const init = this.generateRequestInit()
    const url = this.generateUrl()
    const response = await fetch(url, init)
    const json = (await response.json()) as Response
    return json
  }
}

/** Specialised Fetchers */

export type SearchspringSearchResponse = {
  pagination: SearchspringPagination
  sorting: SearchspringSorting
  results: SearchspringSearchResult[]
  merchandising: SearchspringMerchandising
  facets: SearchspringFacet[]
  responseId?: string
  resultId?: string
  tid?: string
}

/**
 * Searchspring Autocomplete Fetcher
 * For handling completing incomplete search queries.
 */

type SearchspringAutocompleteOptions = {
  query: string
  originalQuery?: string
  resultsPerPage?: number
}

export class SearchspringAutocomplete extends SearchspringFetcher<SearchspringSearchResponse> {
  generateUrl() {
    const endpoint = "/api/search/autocomplete.json"
    return super.generateUrl(endpoint)
  }

  updateParams(options: SearchspringAutocompleteOptions) {
    const { query, originalQuery, resultsPerPage } = options ?? {}

    this.params.setDomainParam()
    this.params.set("resultsFormat", "native")
    this.params.set("redirectResponse", "full")

    this.params.set("q", query)
    if (resultsPerPage)
      this.params.set("resultsPerPage", resultsPerPage.toString())
    if (originalQuery) this.params.set("oq", originalQuery)

    super.updateParams()
  }
}

/**
 * Searchspring Collection Fetcher
 * For handling fetching collection data.
 */

type SearchspringCollectionOptions = {
  handle: string
  originalQuery?: string
  sort?: string
  filters?: string[]
  page?: number
  resultsPerPage?: number
}

export class SearchspringCollection extends SearchspringFetcher<SearchspringSearchResponse> {
  generateUrl() {
    // Category pages must use the Category API, not search.json. Searchspring's
    // Categories 2.0 report only attributes beacon category/* events whose
    // responseId originates from category.json; a search.json responseId is
    // accepted (200) but silently dropped from the report. Response shape is
    // identical to search.json, so downstream parsing is unaffected.
    const endpoint = "/api/search/category.json"
    return super.generateUrl(endpoint)
  }

  updateParams(options: SearchspringCollectionOptions) {
    const { handle, originalQuery, sort, filters, page, resultsPerPage } =
      options ?? {}

    this.params.setDomainParam()
    this.params.set("resultsFormat", "native")
    this.params.set("redirectResponse", "full")
    this.params.set("bgfilter.collection_handle", handle)

    if (originalQuery) this.params.set("oq", originalQuery)
    if (page) this.params.set("page", page.toString())
    if (resultsPerPage)
      this.params.set("resultsPerPage", resultsPerPage.toString())

    if (sort) this.params.setSort(sort)
    if (filters) this.params.setFilters(filters)

    super.updateParams()
  }
}

/**
 * Searchspring Search Fetcher
 * For handling fetching search results.
 */

type SearchspringSearchOptions = {
  query: string
  originalQuery?: string
  page?: number
  resultsPerPage?: number
  tag?: string
  filters?: string[]
  sort?: string
}

export class SearchspringSearch extends SearchspringFetcher<SearchspringSearchResponse> {
  generateUrl() {
    const endpoint = "/api/search/search.json"
    return super.generateUrl(endpoint)
  }

  updateParams(options: SearchspringSearchOptions) {
    const { query, originalQuery, page, resultsPerPage, tag, filters, sort } =
      options

    this.params.setDomainParam()
    this.params.set("resultsFormat", "native")
    this.params.set("redirectResponse", "full")
    this.params.set("q", query)

    if (resultsPerPage)
      this.params.set("resultsPerPage", resultsPerPage.toString())
    if (page) this.params.set("page", page.toString())
    if (originalQuery) this.params.set("oq", originalQuery)
    if (tag) this.params.set("tag", tag)

    if (sort) this.params.setSort(sort)
    if (filters) this.params.setFilters(filters)

    super.updateParams()
  }
}

/**
 * Searchspring Product Fetcher
 * For handling fetching a single products data.
 */

type SearchspringProductOptions = {
  handle: string
}

export class SearchspringProduct extends SearchspringFetcher<SearchspringSearchResponse> {
  generateUrl() {
    const endpoint = "/api/search/search.json"
    return super.generateUrl(endpoint)
  }

  updateParams(options: SearchspringProductOptions) {
    const { handle } = options ?? {}

    this.params.setDomainParam()
    this.params.set("resultsFormat", "native")
    this.params.set("redirectResponse", "full")
    this.params.set("resultsPerPage", "1")
    this.params.set("bgfilter.handle", handle)

    super.updateParams()
  }

  normaliseResponse(response: SearchspringSearchResponse) {
    return response
  }
}

/**
 * Searchspring Recommend Fetcher
 * For handling fetching recommended products.
 */

type SearchspringRecommendOptions = {
  sku?: string
  tags?: string
  limit?: number
  profile?: string
  attributes?: string
  products?: string
  handle?: string
  /** Comma-separated cart SKUs. Overrides the page-load cart, which is stale after AJAX cart changes. */
  cart?: string
}

export type SearchspringRecommendResponse = {
  results: {
    mappings: {
      core: SearchspringSearchResult
    }
    attributes: {
      [key: string]: string
    }
    id: string
  }[]
  profile: {
    tag: string
  }
  resultId?: string
  tid?: string
}

export class SearchspringRecommend extends SearchspringFetcher<SearchspringRecommendResponse> {
  generateHeaders() {
    return new Headers({
      Accept: "application/json",
    })
  }

  generateUrl() {
    const endpoint = `/boost/${this.params.siteId}/recommend`
    return super.generateUrl(endpoint)
  }

  updateParams(options: SearchspringRecommendOptions) {
    const { sku, tags, limit, profile, products, handle, cart } = options

    if (sku) this.params.set("products", sku)
    if (tags) this.params.set("tags", tags)
    if (limit) this.params.set("limits", limit.toString())
    if (profile) this.params.set("tags", profile)
    if (handle) this.params.set("handle", handle)
    if (products) this.params.set("products", products)

    this.params.setViewedProducts()
    if (cart) this.params.set("cart", cart)
    else this.params.setCartItems()
    this.params.setShopperID()

    super.updateParams()
  }
}

/**
 * Searchspring Suggest Fetcher
 * For handling fetching search suggestions.
 */

type SearchspringSuggestOptions = {
  query: string
}

export type SearchspringSuggestResponse = {
  query: string
  suggested: {
    text: string
    type: "completed" | "exact" | "fallback"
    completed?: {
      query: string
      token: string
      type: string
      source: "popular-query" | "name" | "sku"
    }
  }
  alternatives: {
    popularity: number
    text: string
  }[]
  "corrected-query": string
}

export class SearchspringSuggest extends SearchspringFetcher<SearchspringSuggestResponse> {
  generateUrl() {
    const endpoint = "/api/suggest/query"
    return super.generateUrl(endpoint)
  }

  updateParams(options: SearchspringSuggestOptions) {
    const { query } = options ?? {}

    this.params.set("language", "en")
    this.params.set("suggestionCount", "4")
    this.params.set("productCount", "5")

    if (query) this.params.set("q", query)

    super.updateParams()
  }
}

/**
 * Searchspring Trending Fetcher
 * For handling fetching trending search suggestions.
 */

export type SearchspringTrendingResponse = {
  trending: {
    queries: {
      searchQuery: string
      popularity: number
    }[]
  }
}

export type CartRecommendationsResponse = {
  profile: { [key: string]: string }
  results: CartRecommendations[]
}
export type CartRecommendations = {
  attributes: { [key: string]: string }
  id: string
  mappings: {
    core: {
      brand: string
      msrp: number
      name: string
      price: number
      sku: string
      uid: string
      thumbnailImageUrl?: string
      imageUrl?: string
      url: string
    }
  }
}

export class SearchspringTrending extends SearchspringFetcher<SearchspringTrendingResponse> {
  generateUrl() {
    const endpoint = "/api/suggest/trending"
    return super.generateUrl(endpoint)
  }

  updateParams() {
    this.params.set("language", "en")
    this.params.set("limit", "4")

    super.updateParams()
  }
}
